import { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation, withTranslation } from 'react-i18next';
import compose from 'ramda/src/compose';
import { Box, Collapse, Paper, Typography } from '@material-ui/core';
import LocaleKeys from '../../locales/keys';
import { useRetrieveSubscriber } from '../../common/hooks/useRetrieveSubscriber';
import { useUpdateSubscriber } from '../../common/hooks/useUpdateSubscriber';
import { useUpdateCredentials } from '../../common/hooks/useUpdateCredentials';
import NameForm from './formControls/name';
import { useSelector } from 'react-redux';
import BirthdayForm from './formControls/birthday';
import CountryForm from './formControls/country';
import EmailAddressForm from './formControls/emailAddress';
import PasswordForm from './formControls/password';
import ReceiveLatestNewsForm from './formControls/receivedLatestNews';
import TransitionHeader from './header/transitionHeader';
import FadeHeader from './header/fadeHeader';
import { isEmpty } from 'ramda';
import CONSTANTS from '../../constants/externalReferences';
import { FORM_NAMES } from '../../constants/forms';
import {
  getAdditionalProperties,
  getUpdateSubscriberPayload,
  isUpdateCred,
} from '../helpers/accountInfoFormHelpers';
import useMetaDataCodeTables from '../../common/hooks/useMetaDataCodeTables';
import { formatDateString, getLocalDateFormat } from '../../utils/common';
import { payloadTypes } from '../../constants/metaDataPayloadTypes';
import postMessage from '../../utils/postMessage';
import ErrorScreen from '../../common/errorScreen';

const useStyles = makeStyles((theme) => ({
  root: {
    '&.MuiCollapse-hidden': {
      display: 'none',
    },
  },
  submitButton: {
    display: 'block',
    marginLeft: 'auto',
    paddingLeft: '15px',
    paddingRight: '15px',
  },
  link: {
    marginLeft: '8px',
    '&:hover': {
      cursor: 'pointer',
      color: theme.palette.primary.main,
    },
  },
  headerText: {
    textTransform: 'uppercase',
  },
  panelNode: {
    justifyContent: 'space-between',
    display: 'flex',
    width: '100%',
  },
  buttonNode: {
    borderBottom: 'solid 2px #e00',
    borderRadius: '0',
    padding: '0',
    lineHeight: 1,
    textTransform: 'capitalize',
    fontSize: '13px',
    fontWeight: 500,
  },
  baseButtoNode: {
    transform: 'none',
    height: '100%',
    minWidth: 'fit-content',
  },
  boxInnerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: '20px',
  },
  paperContainer: {
    borderTop: 'none',
    borderRight: 'none',
    borderLeft: 'none',
    padding: '15px 15px',
    borderBottom: `1px solid ${theme.palette.custom.lightBlack}`,
    backgroundColor: theme.palette.background.default,
    '& .MuiPaper-root': {
      backgroundColor: theme.palette.background.default,
    },
  },
  heading: {
    fontFamily: 'Formula1-Bold',
    fontSize: '18px',
  },
  divider: {
    borderBottom: `1px solid ${theme.palette.custom.divider}`,
  },
}));

function AccountInfoForm(props) {
  const { profileType, setIsReady } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const animationInMilliSeconds = 350;
  const timeout = {
    appear: animationInMilliSeconds,
    enter: animationInMilliSeconds,
    exit: animationInMilliSeconds,
  };
  const events = {
    name: {
      event: 'save.account.name',
      payload: 'subscriber',
    },
    emailAddress: {
      event: 'account-email-updated',
      payload: 'login',
    },
  };

  /**useRef */
  const elementBoxContainerRef = useRef(null);
  const setBirthdayFocusref = useRef(null);
  const setNameFocusref = useRef(null);
  const setFocusref = useRef(null);
  const setFocusPasswordref = useRef(null);

  /** useSelector */
  const language = useSelector((state) => state.ascendon.settings.language);

  /** useSate */

  const [externalData, setExternalData] = useState(null);
  const [toggleBox, setToggleBox] = useState({
    name: false,
    birthday: false,
    countryOfResidence: false,
    emailAddress: false,
    password: false,
  });
  const [retrieveSubscriberDetails, setRetrieveSubscriberDetails] = useState(
    {},
  );
  const [headingNameValue, setHeadingNameValue] = useState('');
  const [countryLabel, setCountryLabel] = useState('');
  const [nameForm, setNameForm] = useState({
    FirstName: '',
    LastName: '',
    Title: '',
  });
  const [dateOfBirth, setDateOfBirth] = useState({
    BirthDate: '',
  });
  const [countryOfResidence, setCountryOfResidence] = useState({
    country: '',
  });
  const [emailAddress] = useState({
    newEmailAddress: '',
    confirmNewEmailAddress: '',
    currentPassword: '',
  });
  const [password] = useState({
    currentPassword: '',
    newPassword: '',
  });
  const [receivedLatestNews, setReceivedLatestNews] = useState({
    receivedLatestNews: '',
  });
  const [passwordCredentialError, setPasswordCredentialError] = useState('');
  const [emailCredentialError, setEmailCredentialError] = useState('');
  const [currentClickName, setCurrentClickName] = useState({});
  const [emailAddressValue, setEmailAddressValue] = useState('');
  const [errors, setErrors] = useState([]);
  /** Web Api Hooks */
  const [
    updateCredentialsResponse,
    updateCredentialsFault,
    requestUpdateCredentials,
  ] = useUpdateCredentials();
  const [
    updateSubscriberResponse,
    updateSubscriberFault,
    requestUpdateSubscriber,
  ] = useUpdateSubscriber();
  const [
    retrieveSubscriberResponse,
    retrieveSubscriberFault,
    requestRetrieveSubscriber,
  ] = useRetrieveSubscriber();

  const [
    retrieveMetaDataCodeTablesResponse,
    retrieveMetaDataCodeTablesFault,
    requestMetaDataCodeTables,
  ] = useMetaDataCodeTables();

  /** Helper functions */

  const setToogleBoxContainer = (eventType, isActive) => {
    const getCheckedValue = { ...toggleBox, [eventType]: isActive };
    setToggleBox(getCheckedValue);
  };

  const credentialsErrorNode = (message, currentSaveName) => {
    if (currentSaveName === FORM_NAMES.EMAILADDRESS) {
      setEmailCredentialError(message);
    }
    if (currentSaveName === FORM_NAMES.PASSWORD) {
      setPasswordCredentialError(message);
    }
  };

  const setEmailAddress = (value) => {
    if (value) {
      setEmailAddressValue(value);
    }
  };

  const getEventPayload = (payload, subscriber) => {
    let result = {};
    switch (payload) {
      case 'subscriber':
        result = subscriber;
        break;
      case 'login':
        result = {
          Email: subscriber?.Email,
          Login: subscriber?.Login,
        };
        break;
      default:
        result = {};
    }
    return result;
  };

  /** useEffects */

  /** Retrieve Subscriber */
  useEffect(() => {
    requestRetrieveSubscriber(); // async
  }, []);

  useEffect(() => {
    if (retrieveSubscriberResponse) {
      setRetrieveSubscriberDetails(retrieveSubscriberResponse.Subscriber);
      postMessage('module.hideLoader', {});
      setIsReady(true);
    }
  }, [retrieveSubscriberResponse]);

  useEffect(() => {
    if (retrieveSubscriberFault) {
      postMessage('module.hideLoader', {});
      setIsReady(true);
      setErrors([retrieveSubscriberFault]);
    }
  }, [retrieveSubscriberFault]);

  useEffect(() => {
    if (retrieveMetaDataCodeTablesFault) {
      postMessage('module.hideLoader', {});
      setIsReady(true);
      setErrors([retrieveMetaDataCodeTablesFault]);
    }
  }, [retrieveMetaDataCodeTablesFault]);

  /** Update Subscriber */

  useEffect(() => {
    if (updateSubscriberResponse?.Subscriber) {
      const { name } = currentClickName;
      setToogleBoxContainer(name, false);
      // requestRetrieveSubscriber(); // since the response of the updateSubscriberResponse is equal to the retrieveSubscriberResponse, set up the values to the RetrieveSubscriberDetails;
      setRetrieveSubscriberDetails(updateSubscriberResponse.Subscriber);

      const candidateEvent = events[name];
      if (candidateEvent) {
        const payload = getEventPayload(
          candidateEvent.payload,
          updateSubscriberResponse.Subscriber,
        );
        postMessage(candidateEvent.event, payload);
      }
      postMessage('module.hideLoader');
    }
  }, [updateSubscriberResponse]);

  useEffect(() => {
    if (updateSubscriberFault) {
      setErrors([updateSubscriberFault]);
      postMessage('module.hideLoader');
    }
  }, [updateSubscriberFault]);

  /** Update Credentials */

  useEffect(() => {
    if (updateCredentialsResponse) {
      const { name, formik } = currentClickName;

      const updatedSubscriberDetails = {
        ...retrieveSubscriberDetails,
        Login: emailAddressValue,
        Email: emailAddressValue,
      };

      setRetrieveSubscriberDetails(updatedSubscriberDetails);
      setToogleBoxContainer(name, false);
      formik?.resetForm();
      credentialsErrorNode('', name);

      const candidateEvent = events[name];
      if (candidateEvent) {
        const payload = getEventPayload(
          candidateEvent.payload,
          updatedSubscriberDetails,
        );
        postMessage(candidateEvent.event, payload);
      }
      postMessage('module.hideLoader');
    }
  }, [updateCredentialsResponse]);

  /** update Credential fault message set the api fault error in the UI*/
  useEffect(() => {
    if (updateCredentialsFault) {
      const { name } = currentClickName;
      credentialsErrorNode(updateCredentialsFault, name);
      postMessage('module.hideLoader');
    }
  }, [updateCredentialsFault]);

  /** Set Form Values based on the  RetrieveSubscriber Response*/
  useEffect(() => {
    if (isEmpty(retrieveSubscriberDetails)) {
      return;
    }
    let getCountryNode = getAdditionalProperties(
      retrieveSubscriberDetails?.AdditionalProperties,
      CONSTANTS.MARKETING_COUNTRY,
    )?.Values[0];
    if (!getCountryNode) {
      getCountryNode =
        retrieveSubscriberDetails?.Country ||
        retrieveSubscriberDetails?.HomeCountry;
    }

    const getReceivedLatestNews = getAdditionalProperties(
      retrieveSubscriberDetails?.AdditionalProperties,
      CONSTANTS.CONSENT_CONTACT,
    )?.Values[0];

    setNameForm({
      Title: retrieveSubscriberDetails?.Title,
      FirstName: retrieveSubscriberDetails?.FirstName,
      LastName: retrieveSubscriberDetails?.LastName,
    });

    setDateOfBirth({
      BirthDate: formatDateString(retrieveSubscriberDetails?.BirthDate),
    });

    setCountryOfResidence({
      country: getCountryNode,
    });

    setReceivedLatestNews({
      receivedLatestNews: getReceivedLatestNews === '1',
    });

    const title =
      retrieveSubscriberDetails.Title &&
      t(LocaleKeys.titles[retrieveSubscriberDetails.Title]).indexOf(
        'titles.',
      ) === -1
        ? `${t(LocaleKeys.titles[retrieveSubscriberDetails.Title])} `
        : '';
    setHeadingNameValue(
      retrieveSubscriberDetails?.FirstName &&
        retrieveSubscriberDetails?.LastName
        ? `${title}${retrieveSubscriberDetails?.FirstName || ''} ${
            retrieveSubscriberDetails?.LastName || ''
          }`
        : ``,
    );
  }, [retrieveSubscriberDetails]);

  /** Get External Data Like Country Codes, Title, etc..*/
  useEffect(() => {
    if (Object.keys(retrieveMetaDataCodeTablesResponse || {}).length) {
      setExternalData({ ...retrieveMetaDataCodeTablesResponse });
    }
  }, [retrieveMetaDataCodeTablesResponse]);

  const {
    countries,
    titles,
    subscriberRequirements,
    subscriberLeadSourceCode,
    regex,
  } = payloadTypes;

  useEffect(() => {
    requestMetaDataCodeTables({
      countries,
      titles,
      subscriberRequirements,
      subscriberLeadSourceCode,
      regex,
    });
  }, [profileType]);
  /** Set the Country label based on the external data country value */
  useEffect(() => {
    let getCountryNode = getAdditionalProperties(
      retrieveSubscriberDetails?.AdditionalProperties,
      CONSTANTS.MARKETING_COUNTRY,
    )?.Values[0];
    if (!getCountryNode) {
      getCountryNode =
        retrieveSubscriberDetails?.Country ||
        retrieveSubscriberDetails?.HomeCountry;
    }

    const countries =
      externalData?.countryRules?.data?.Codes || externalData?.countryRules;
    if (countries) {
      const getCountryName = countries.find(
        (item) => item.Value === getCountryNode,
      );
      setCountryLabel(getCountryName?.Name || '');
    }
  }, [externalData, retrieveSubscriberDetails]);

  /** Event Handlers */

  const handleSaveClick = (eventType) => {
    const { name, formik } = eventType;
    if (Object.keys(retrieveSubscriberDetails).length && formik.isValid) {
      const isUpdateCredNode = isUpdateCred(name);
      const localDateFormat = getLocalDateFormat();
      const getUpdateSubscriberPayloadNode = getUpdateSubscriberPayload(
        name,
        retrieveSubscriberDetails,
        formik.values,
        isUpdateCredNode,
        localDateFormat,
      );
      postMessage('module.showLoader');
      if (isUpdateCredNode) {
        setEmailAddress(getUpdateSubscriberPayloadNode.NewLogin);
        requestUpdateCredentials(getUpdateSubscriberPayloadNode);
      } else {
        requestUpdateSubscriber(getUpdateSubscriberPayloadNode);
      }
      setCurrentClickName({ name, formik });
    }
  };

  const handleClick = (eventType) => {
    setToogleBoxContainer(eventType, true);
  };

  const handleCancelClick = (eventType) => {
    const { name, formik } = eventType;
    formik?.resetForm(); // need to call on save for email and password
    setToogleBoxContainer(name, false);
    credentialsErrorNode('', name);
  };

  if (errors && errors.length > 0) {
    return (
      <Box mb={10} mt={3}>
        <Box mb={6}>
          <ErrorScreen
            error={errors[0]}
            mt={5}
            mb={5}
            variant="body1"
            hideBackdrop={true}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={`panel-heading ${classes.divider}`}>
        <Typography variant="h2" className={classes.headerText}>
          {t(LocaleKeys.accountInfo.personalInformationTitle)}
        </Typography>
      </Box>
      <Box className="panel-body">
        <Box className={classes.paper} mb={5}>
          <Paper
            elevation={4}
            variant="outlined"
            square
            className={classes.paperContainer}
            ref={elementBoxContainerRef}
          >
            <TransitionHeader
              headingLabel={t(LocaleKeys.accountInfo.name)}
              buttonLabel={t(LocaleKeys.accountInfo.edit)}
              headingValue={headingNameValue}
              classes={classes}
              fadeIn={toggleBox.name}
              name={FORM_NAMES.NAME}
              clickHandler={handleClick}
            />

            <div className={classes.container}>
              <Collapse
                in={toggleBox.name}
                onEntered={() => {
                  setNameFocusref?.current && setNameFocusref?.current.focus();
                }}
              >
                <Paper elevation={0}>
                  <div className={classes.column} />
                  <div>
                    <NameForm
                      handleCancel={handleCancelClick}
                      handleSave={handleSaveClick}
                      codeTableData={externalData}
                      language={language}
                      formData={nameForm}
                      formName={FORM_NAMES.NAME}
                      autoFocusRef={setNameFocusref}
                    ></NameForm>
                  </div>
                </Paper>
              </Collapse>
            </div>
          </Paper>
        </Box>
        <Box mb={5}>
          <Paper
            elevation={4}
            variant="outlined"
            className={classes.paperContainer}
            square
          >
            <TransitionHeader
              headingLabel={t(LocaleKeys.accountInfo.dateOfBirth)}
              buttonLabel={t(LocaleKeys.accountInfo.edit)}
              headingValue={dateOfBirth.BirthDate}
              classes={classes}
              fadeIn={toggleBox.birthday}
              name={FORM_NAMES.BIRTHDAY}
              clickHandler={handleClick}
            />
            <div className={classes.container}>
              <Collapse
                in={toggleBox.birthday}
                timeout={timeout}
                onEntered={() => {
                  setBirthdayFocusref?.current &&
                    setBirthdayFocusref?.current.focus();
                }}
              >
                <Paper elevation={0}>
                  <div className={classes.column} />
                  <div>
                    <BirthdayForm
                      handleCancel={handleCancelClick}
                      handleSave={handleSaveClick}
                      codeTableData={externalData}
                      language={language}
                      formData={dateOfBirth}
                      formName={FORM_NAMES.BIRTHDAY}
                      autoFocusRef={setBirthdayFocusref}
                    />
                  </div>
                </Paper>
              </Collapse>
            </div>
          </Paper>
        </Box>
        <Box mb={5}>
          <Paper
            elevation={4}
            variant="outlined"
            className={classes.paperContainer}
            square
          >
            <TransitionHeader
              headingLabel={t(LocaleKeys.accountInfo.countryOfResidence)}
              buttonLabel={t(LocaleKeys.accountInfo.edit)}
              headingValue={countryLabel}
              classes={classes}
              fadeIn={toggleBox.countryOfResidence}
              name={FORM_NAMES.COUNTRYOFRESIDENCE}
              clickHandler={handleClick}
            />
            <div className={classes.container}>
              <Collapse in={toggleBox.countryOfResidence} timeout={timeout}>
                <Paper elevation={0}>
                  <div className={classes.column} />
                  <div>
                    <CountryForm
                      handleCancel={handleCancelClick}
                      handleSave={handleSaveClick}
                      codeTableData={externalData}
                      language={language}
                      formData={countryOfResidence}
                      formName={FORM_NAMES.COUNTRYOFRESIDENCE}
                    />
                  </div>
                </Paper>
              </Collapse>
            </div>
          </Paper>
        </Box>
        <Box mb={5}>
          <Paper
            elevation={4}
            variant="outlined"
            className={classes.paperContainer}
            square
          >
            <FadeHeader
              headingLabel={t(LocaleKeys.accountInfo.emailAddress)}
              buttonLabel={t(LocaleKeys.accountInfo.edit)}
              headingValue={retrieveSubscriberDetails?.Email}
              classes={classes}
              fadeIn={toggleBox.emailAddress}
              name={FORM_NAMES.EMAILADDRESS}
              clickHandler={handleClick}
            />
            <div className={classes.container}>
              <Collapse
                in={toggleBox.emailAddress}
                timeout={timeout}
                onEntered={() => {
                  setFocusref?.current && setFocusref?.current.focus();
                }}
              >
                <Paper elevation={0}>
                  <div className={classes.column} />
                  <div>
                    <EmailAddressForm
                      handleCancel={handleCancelClick}
                      handleSave={handleSaveClick}
                      codeTableData={externalData}
                      language={language}
                      formData={emailAddress}
                      formName={FORM_NAMES.EMAILADDRESS}
                      credentialErrorMessage={emailCredentialError}
                      autoFocusRef={setFocusref}
                    />
                  </div>
                </Paper>
              </Collapse>
            </div>
          </Paper>
        </Box>
        <Box mb={5}>
          <Paper
            elevation={4}
            variant="outlined"
            className={classes.paperContainer}
            square
          >
            <FadeHeader
              headingLabel={t(LocaleKeys.accountInfo.password)}
              buttonLabel={t(LocaleKeys.accountInfo.edit)}
              headingValue="••••••••••"
              classes={classes}
              fadeIn={toggleBox.password}
              name={FORM_NAMES.PASSWORD}
              clickHandler={handleClick}
            />
            <div className={classes.container}>
              <Collapse
                in={toggleBox.password}
                timeout={timeout}
                onEntered={() => {
                  setFocusPasswordref?.current &&
                    setFocusPasswordref?.current.focus();
                }}
              >
                <Paper elevation={0}>
                  <div className={classes.column} />
                  <div>
                    <PasswordForm
                      handleCancel={handleCancelClick}
                      handleSave={handleSaveClick}
                      codeTableData={externalData}
                      language={language}
                      formData={password}
                      formName={FORM_NAMES.PASSWORD}
                      credentialErrorMessage={passwordCredentialError}
                      autoFocusRef={setFocusPasswordref}
                    />
                  </div>
                </Paper>
              </Collapse>
            </div>
          </Paper>
        </Box>
        <Box>
          <ReceiveLatestNewsForm
            handleSave={handleSaveClick}
            codeTableData={externalData}
            language={language}
            formData={receivedLatestNews}
            formName={FORM_NAMES.RECEIVEDLATESTNEWS}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default compose(withTranslation())(AccountInfoForm);
